import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, message } from 'antd';
import { MinusCircleOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import { truncateAddressToCity } from 'utils/addressHelpers';

const SequenceContainer = styled.div`
	border: 1px solid #e4e7ec;
	background: #f8fafc;
	padding: 16px;
	border-radius: 8px;
`;

const SequenceHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
`;

const SequenceHeading = styled.h3`
	margin: 0;
	font-size: 28px;
	font-weight: 600;
	line-height: 36px;
	color: #101828;
`;

const SequenceHint = styled.span`
	font-size: 12px;
	color: #98a2b3;
`;

const NodeList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const SequenceRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	min-height: 52px;
	background: #fff;
	border: 1px solid ${(props) => (props.$isDragOver ? '#98a2b3' : '#d0d5dd')};
	border-radius: 8px;
	padding: 10px 12px;
	opacity: ${(props) => (props.$isDragging ? 0.55 : 1)};
`;

const SequenceLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1;
	min-width: 0;
`;

const SequenceRight = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
`;

const DragHandle = styled.button`
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${(props) => (props.$active ? '#18405d' : '#94a3b8')};
	cursor: grab;
	padding: 0;

	&:active {
		cursor: grabbing;
	}
`;

const SequenceIndex = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 4px;
	font-size: 11px;
	line-height: 1;
	font-weight: 600;
	background: #18405d;
	color: #fff;
	border-radius: 4px;
`;

const NodeField = styled.div`
	flex: 1;
	min-width: 0;
	border: none;

	> div {
		border: none !important;
		box-shadow: none !important;
		background: transparent !important;
	}

	.ant-input-affix-wrapper,
	.ant-input-affix-wrapper-focused,
	.ant-input-outlined,
	input.ant-input,
	.ant-input {
		border: none !important;
		box-shadow: none !important;
		outline: none !important;
		background: transparent !important;
		padding-left: 0;
		padding-right: 34px;
		font-size: 14px;
		color: #111827;
	}

	.ant-input-affix-wrapper {
		padding: 0 !important;
	}

	.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled),
	.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover,
	.ant-input-affix-wrapper:hover,
	.ant-input:hover,
	.ant-input:focus {
		border: none !important;
		box-shadow: none !important;
		outline: none !important;
	}
`;

const SequenceTag = styled.span`
	font-size: 10px;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 6px;
	background: ${(props) => (props.$variant === 'end' ? '#fee2e2' : '#dcfce7')};
	color: ${(props) => (props.$variant === 'end' ? '#b42318' : '#067647')};
`;

const RemoveBtn = styled.button`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: none;
	background: transparent;
	color: #98a2b3;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: color 0.2s ease, transform 0.2s ease;

	&:hover {
		color: #667085;
		transform: scale(1.04);
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.4;
	}

	svg {
		font-size: 20px;
	}
`;

const AddStopButton = styled.button`
	margin-top: 12px;
	width: 100%;
	border: 1px dashed #d0d5dd;
	background: #fff;
	color: #18405d;
	padding: 10px 14px;
	border-radius: 8px;
	font-size: 13px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	cursor: pointer;

	&:hover {
		border-color: #98a2b3;
		background: #f8fafc;
	}

	&:disabled {
		color: #9ca3af;
		border-color: #e5e7eb;
		background: #f9fafb;
		cursor: not-allowed;
	}
`;

const MAX_INTERMEDIARY_NODES = 5;
const ROUND_TRIP = 'round_trip';
const CURSOR_NOT_ALLOWED = 'not-allowed';

const generateId = () => Math.random().toString(36).substring(7);

const getNodeType = (idx, length) => {
	if (idx === 0) return 'start';
	if (idx === length - 1) return 'end';
	return 'intermediary';
};

const formatAddress = (address = '') => address.trim().toLowerCase();

const isAllowedDuplicate = ({ currentIndex, compareIndex, nodes, routeType }) =>
	routeType === ROUND_TRIP &&
	((currentIndex === 0 && compareIndex === nodes.length - 1) ||
		(currentIndex === nodes.length - 1 && compareIndex === 0));

const createDefaultNodes = () => [
	{ id: generateId(), address: '', type: 'start' },
	{ id: generateId(), address: '', type: 'intermediary' },
	{ id: generateId(), address: '', type: 'end' },
];

const applyRoundTripMirror = (nodes, routeType) => {
	const safeNodes = nodes || [];
	if (routeType !== ROUND_TRIP || safeNodes.length <= 1) {
		return safeNodes;
	}

	const mirroredNodes = [...safeNodes];
	mirroredNodes[mirroredNodes.length - 1] = {
		...mirroredNodes[mirroredNodes.length - 1],
		address: mirroredNodes[0]?.address || '',
		fullAddress: mirroredNodes[0]?.fullAddress || mirroredNodes[0]?.address || '',
		pincode: mirroredNodes[0]?.pincode || '',
		city: mirroredNodes[0]?.city || '',
	};
	return mirroredNodes;
};

const buildInitialNodes = (initialNodes = []) =>
	initialNodes.length > 0
		? initialNodes.map((node, idx) => ({
				...node,
				id: node.id || generateId(),
				type: getNodeType(idx, initialNodes.length),
		  }))
		: createDefaultNodes();

export default function NodeSequenceModal({
	visible,
	onClose,
	onConfirm,
	initialNodes = [],
	disabled = false,
	routeType = 'one_way',
}) {
	const [nodes, setNodes] = useState([]);
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [dragOverIndex, setDragOverIndex] = useState(null);
	const [dragEnabledIndex, setDragEnabledIndex] = useState(null);

	useEffect(() => {
		if (!visible) return;
		setNodes(applyRoundTripMirror(buildInitialNodes(initialNodes), routeType));
	}, [initialNodes, routeType, visible]);

	const updateNodeAddress = (index, data) => {
		const selectedAddress = truncateAddressToCity(data.address || '', data.city || '').trim();
		const hasDuplicateAddress = nodes.some(
			(node, nodeIndex) =>
				nodeIndex !== index &&
				!isAllowedDuplicate({
					currentIndex: index,
					compareIndex: nodeIndex,
					nodes,
					routeType,
				}) &&
				formatAddress(node.address) &&
				formatAddress(node.address) === formatAddress(selectedAddress)
		);

		if (selectedAddress && hasDuplicateAddress) {
			message.warning('Each node must have a different address');
			return;
		}

		setNodes((prev) => {
			const updated = [...prev];
			updated[index] = {
				...updated[index],
				address: selectedAddress,
				fullAddress: data.fullAddress || data.address || '',
				pincode: data.pincode || '',
				city: data.city || '',
			};
			return applyRoundTripMirror(updated, routeType);
		});
	};

	const handleAddNode = (afterIndex) => {
		setNodes((prev) => {
			const currentIntermediaryCount = prev.filter((n) => n.type === 'intermediary').length;
			if (currentIntermediaryCount >= MAX_INTERMEDIARY_NODES) {
				message.warning(`Maximum ${MAX_INTERMEDIARY_NODES} intermediate stops allowed`);
				return prev;
			}

			const updated = [...prev];
			updated.splice(afterIndex + 1, 0, {
				id: generateId(),
				address: '',
				type: 'intermediary',
			});
			return applyRoundTripMirror(
				updated.map((node, idx) => ({
					...node,
					type: getNodeType(idx, updated.length),
				})),
				routeType
			);
		});
	};

	const handleRemoveNode = (index) => {
		setNodes((prev) => {
			if (prev.length <= 2) return prev;
			const updated = prev.filter((_, i) => i !== index);
			return applyRoundTripMirror(
				updated.map((node, i) => ({
					...node,
					type: getNodeType(i, updated.length),
				})),
				routeType
			);
		});
	};

	const handleReset = () => {
		setNodes(applyRoundTripMirror(buildInitialNodes(initialNodes), routeType));
	};

	const handleDragStart = (e, index) => {
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/plain', index.toString());
	};

	const handleDragOver = (e, index) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		if (draggedIndex === null || draggedIndex === index) return;
		if (dragOverIndex !== index) {
			setDragOverIndex(index);
		}
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
		setDragOverIndex(null);
		setDragEnabledIndex(null);
	};

	const handleDrop = (e, targetIndex) => {
		e.preventDefault();
		if (draggedIndex === null || draggedIndex === targetIndex) {
			handleDragEnd();
			return;
		}

		setNodes((prev) => {
			const newNodes = [...prev];
			const draggedItem = newNodes[draggedIndex];
			newNodes.splice(draggedIndex, 1);
			newNodes.splice(targetIndex, 0, draggedItem);
			return applyRoundTripMirror(
				newNodes.map((node, i) => ({
					...node,
					type: getNodeType(i, newNodes.length),
				})),
				routeType
			);
		});
		handleDragEnd();
	};

	const checkForDuplicateAddresses = (validNodes) => {
		const uniqueAddresses = new Set();
		return validNodes.some((node, index) => {
			const normalizedAddress = formatAddress(node.address);
			if (!normalizedAddress) return false;
			if (
				routeType === ROUND_TRIP &&
				((index === 0 &&
					formatAddress(validNodes[validNodes.length - 1]?.address) ===
						normalizedAddress) ||
					(index === validNodes.length - 1 &&
						formatAddress(validNodes[0]?.address) === normalizedAddress)) &&
				index === validNodes.length - 1
			) {
				return false;
			}
			if (uniqueAddresses.has(normalizedAddress)) return true;
			uniqueAddresses.add(normalizedAddress);
			return false;
		});
	};

	const handleConfirm = () => {
		const startNode = nodes[0];
		const endNode = nodes[nodes.length - 1];

		if (!startNode?.address) {
			message.warning('Please select a start location');
			return;
		}
		if (!endNode?.address) {
			message.warning('Please select an end location');
			return;
		}

		const normalizedNodes = applyRoundTripMirror(nodes, routeType);
		const validNodes = normalizedNodes.filter(
			(node, idx) =>
				idx === 0 ||
				idx === normalizedNodes.length - 1 ||
				(node.address && node.address.trim())
		);

		if (checkForDuplicateAddresses(validNodes)) {
			message.warning('Each node must have a different address');
			return;
		}

		onConfirm(
			validNodes.map((node, idx) => ({
				...node,
				type: getNodeType(idx, validNodes.length),
			}))
		);
	};

	const intermediaryCount = nodes.filter((node) => node.type === 'intermediary').length;
	const hasReachedMax = intermediaryCount >= MAX_INTERMEDIARY_NODES;

	return (
		<Modal
			title="Node Sequence"
			open={visible}
			onCancel={onClose}
			footer={[
				<Button
					key="reset"
					danger
					ghost
					onClick={handleReset}
					disabled={disabled}
					style={{ minWidth: 160 }}
				>
					Reset Selection
				</Button>,
				<Button
					key="confirm"
					type="primary"
					onClick={handleConfirm}
					disabled={disabled}
					style={{ minWidth: 140 }}
				>
					Confirm
				</Button>,
			]}
			width={680}
			destroyOnClose
		>
			<SequenceContainer>
				<SequenceHeader>
					<SequenceHeading>Arrange Sequence</SequenceHeading>
					<SequenceHint>Drag to reorder</SequenceHint>
				</SequenceHeader>
				<NodeList>
					{nodes.map((node, index) => {
						const isStart = node.type === 'start';
						const isEnd = node.type === 'end';
						const isRoundTripEndLocked =
							routeType === ROUND_TRIP && isEnd && nodes.length > 1;
						const canRemove = !isStart && !isEnd && nodes.length > 2;

						return (
							<SequenceRow
								key={node.id || `node-${index}`}
								draggable={
									!disabled && !isRoundTripEndLocked && dragEnabledIndex === index
								}
								onDragStart={(e) => handleDragStart(e, index)}
								onDragOver={(e) => handleDragOver(e, index)}
								onDrop={(e) => handleDrop(e, index)}
								onDragEnd={handleDragEnd}
								$isDragOver={dragOverIndex === index}
								$isDragging={draggedIndex === index}
							>
								<SequenceLeft>
									<DragHandle
										type="button"
										$active={dragEnabledIndex === index}
										onMouseDown={() =>
											!disabled &&
											!isRoundTripEndLocked &&
											setDragEnabledIndex(index)
										}
										onMouseUp={() => setDragEnabledIndex(null)}
										onMouseLeave={() => setDragEnabledIndex(null)}
										title="Drag to reorder"
										disabled={isRoundTripEndLocked}
										style={{
											cursor: isRoundTripEndLocked
												? CURSOR_NOT_ALLOWED
												: 'grab',
											opacity: isRoundTripEndLocked ? 0.45 : 1,
										}}
									>
										<HolderOutlined />
									</DragHandle>
									<SequenceIndex>{index + 1}</SequenceIndex>
									<NodeField>
										<AddressPickerV2
											value={node.address || ''}
											onChange={(data) => updateNodeAddress(index, data)}
											placeholder="Add Location"
											disabled={disabled || isRoundTripEndLocked}
										/>
									</NodeField>
								</SequenceLeft>
								<SequenceRight>
									{isStart && <SequenceTag $variant="start">START</SequenceTag>}
									{isEnd && <SequenceTag $variant="end">END</SequenceTag>}
									{!isRoundTripEndLocked && (
										<RemoveBtn
											type="button"
											onClick={() => handleRemoveNode(index)}
											disabled={disabled || !canRemove}
											title={canRemove ? 'Remove stop' : ''}
											style={{
												visibility: canRemove ? 'visible' : 'hidden',
											}}
										>
											<MinusCircleOutlined />
										</RemoveBtn>
									)}
								</SequenceRight>
							</SequenceRow>
						);
					})}
				</NodeList>
				<AddStopButton
					type="button"
					onClick={() => handleAddNode(nodes.length - 2)}
					disabled={disabled || hasReachedMax}
				>
					<PlusOutlined />
					Add Stop ({intermediaryCount}/{MAX_INTERMEDIARY_NODES})
				</AddStopButton>
			</SequenceContainer>
		</Modal>
	);
}

NodeSequenceModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	initialNodes: PropTypes.arrayOf(
		PropTypes.shape({
			address: PropTypes.string,
			type: PropTypes.oneOf(['start', 'intermediary', 'end']),
		})
	),
	disabled: PropTypes.bool,
	routeType: PropTypes.oneOf(['one_way', 'round_trip']),
};
